import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  GridItem,
  Button,
  Modal,
  Container,
  Tabs,
  Tab
} from "@sainsburys-tech/fable";
import StoreDetailsPageHeader from "./StoreDetailsPageHeader";
import DeviceList from "../DeviceList";
import DeviceFilter from "../DeviceFilter";
import LoadingWrapper from "../LoadingWrapper";
import StoreLightingControlPanel from "../StoreLightingControlPanel";
import WhenAuthorized from "../WhenAuthorized";
import ErrorMessage from "../ErrorMessage";
import * as roles from "../../utils/roles";
import EnergyMainMeterSensorEnergyChart from "../EnergyMainMeterSensorEnergyChart";
import NotFoundView from "../NotFoundView";
import StoreSceneLevelsTable from "../StoreSceneLevelsTable";
import StoreOverridesTable from "../StoreOverridesTable";
import StoreLightingStatusPanel from "../StoreLightingStatusPanel";
import moment from "moment";
import ManagersOverrideBanner from "../ManagersOverrideBanner";
import ManagersOverridePendingBanner from "../ManagersOverridePendingBanner";
import * as managersOverrideStatus from "../../model/lighting-overrides/managersOverrideStatus";
const validTabs = ["energy", "lighting"];

class StoreDetailsPage extends Component {
  state = {
    filterModalVisible: false
  };

  setActiveTab = tabKey => {
    this.props.onTabChange(tabKey);
  };

  loadDevices = () => {
    const storeId = this.props.store.storeId;
    this.props.loadDevices(storeId);
  };

  handleFilterModalOpen = () => {
    this.setState({ filterModalVisible: true });
  };

  handleFilterModalClose = () => {
    this.loadDevices();
    this.setState({ filterModalVisible: false });
  };

  handleFilterUpdated = filter => {
    this.props.handleFilterChange(filter);
    if (!this.isMobile()) {
      this.loadDevices(this.props.store.storeId);
    }
  };

  mapZoneSceneLevels = zoneConfiguration => {
    if (zoneConfiguration === null || zoneConfiguration === undefined) {
      return;
    }
    return zoneConfiguration.map(zoneConfig => {
      return {
        zoneName: zoneConfig.zone.name,
        trading: this.getSceneLevel(zoneConfig, "Trading"),
        lowFootfall: this.getSceneLevel(zoneConfig, "LowFootfall"),
        stocking: this.getSceneLevel(zoneConfig, "Stocking"),
        priceSpike: this.getSceneLevel(zoneConfig, "PriceSpike")
      };
    });
  };

  getSceneLevel = (zoneConfig, scene) => {
    const isOn = zoneConfig.scenes.find(x => x.scene.name === scene)
      .defaultValue.isOn;

    const defaultLevelPercentage = zoneConfig.scenes.find(
      x => x.scene.name === scene
    ).defaultValue.levelPercentage;

    const luxBands = zoneConfig.scenes.find(x => x.scene.name === scene)
      .luxBands;

    let levelPercentage = "";

    if (luxBands) {
      const minLevelPercentage = Math.min.apply(
        Math,
        luxBands.map(lx => {
          return lx.levelPercentage;
        })
      );

      levelPercentage = minLevelPercentage + "-" + defaultLevelPercentage;
    } else {
      levelPercentage = defaultLevelPercentage;
    }

    if (isOn !== undefined) {
      return isOn === true ? "On" : "Off";
    } else {
      return levelPercentage + "%";
    }
  };

  mapSchedule = (storeConfiguration, openingHours) => {
    if (
      storeConfiguration === null ||
      storeConfiguration === undefined ||
      (openingHours.opens === null && openingHours.closes === null)
    ) {
      return;
    }
    return storeConfiguration.zones.map(zone => {
      return {
        zoneName: zone.zone.name,
        stocking1: "00:00 - " + openingHours.opens.substring(0, 5),
        trading:
          openingHours.opens.substring(0, 5) +
          " - " +
          openingHours.closes.substring(0, 5),
        stocking2: openingHours.closes.substring(0, 5) + " - 00:00"
      };
    });
  };

  mapOverrides = overrides => {
    if (overrides === undefined) {
      return;
    }
    return overrides.map(ov => {
      return {
        overrideId: ov.overrideId,
        overrideName: ov.name,
        zones: ov.overrideAllZones ? "All" : `${ov.zones.length}`,
        start: ov.start,
        end: ov.end,
        created: ov.created,
        stage: ov.overrideStages.find(
          x => x.storeId === this.props.store.storeId
        )
      };
    });
  };

  getActiveScene(openingHours) {
    const storeOpen = new moment().format("YYYY-MM-DD[T]" + openingHours.opens);
    const storeCloses = new moment().format(
      "YYYY-MM-DD[T]" + openingHours.closes
    );

    if (
      new moment().isAfter(moment(storeOpen)) &&
      new moment().isBefore(moment(storeCloses))
    ) {
      return "Open";
    }

    return "Closed";
  }

  isMobile = () => {
    return window.innerWidth < 1200;
  };

  managersOverrideStatus = () => {
    if (
      new moment().subtract(10, "minutes") >
      new moment(this.props.storeStatus.gatewayLastOnline)
    ) {
      return managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_OFF;
    }

    const setGatewayInControlCommandSent = moment(
      this.props.storeStatus.setGatewayInControlCommandSent
    );

    if (new moment().diff(setGatewayInControlCommandSent, "minutes") < 12) {
      return managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_DISABLE_PENDING;
    }

    if (
      new moment().subtract(10, "minutes") <=
        new moment(this.props.storeStatus.gatewayLastOnline) &&
      this.props.storeStatus.managersOverrideEngaged
    ) {
      return managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_ON;
    }

    return managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_OFF;
  };

  render() {
    let energySubmeterDevices = [];
    if (this.props.devices && this.props.devices.length !== 0) {
      energySubmeterDevices = this.props.devices.filter(
        device => device.deviceType === "EnergySubMeter"
      );
    }

    const energyMainMeterDevice = this.props.devices.filter(
      device => device.deviceType === "EnergyMainMeter"
    )[0];

    const energyMainMeterSensor =
      energyMainMeterDevice &&
      energyMainMeterDevice.sensors.filter(
        sensor => sensor.name === "Main Meter Aggregate kWh"
      )[0];

    if (
      this.props.queryParams.hasOwnProperty("tab") &&
      !validTabs.includes(this.props.queryParams.tab)
    ) {
      return <NotFoundView />;
    }

    return (
      <div>
        <StoreDetailsPageHeader
          {...this.props}
          showLightingPanel={this.props.showLightingPanel}
          store={this.props.store}
          loadFailed={this.props.lightingLoadFailed}
        />
        <Tabs activeTab={this.props.activeTab}>
          <Tab
            value="energy"
            label="Energy"
            isSelected={this.props.activeTab === "energy"}
            onClick={() => this.setActiveTab("energy")}
          >
            <div style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)", padding: "20px" }}>
              <div>
                {energyMainMeterSensor && (
                  <Container>
                    <EnergyMainMeterSensorEnergyChart
                      sensorId={energyMainMeterSensor.sensorId}
                      storeId={this.props.store.storeId}
                    />
                  </Container>
                )}
                <Grid cols={{ xs: 1, lg: 4 }}>
                  <GridItem
                    colSpan={{ xs: 4, lg: 1 }}
                    className="cb-c-store-details-page__filter-list"
                  >
                    <DeviceFilter
                      {...this.props}
                      tagHierarchy={this.props.tagHierarchy}
                      handleFilterUpdated={this.handleFilterUpdated}
                    />
                  </GridItem>
                  <GridItem colSpan={{ xs: 4, lg: 3 }}>
                    <Grid cols={{ xs: 1, lg: 2 }} className="cb-u-component-header fable:mb-8">
                      <GridItem colSpan="1/2">
                        <h4 className="cb-u-component-header-title">Devices</h4>
                      </GridItem>
                      <GridItem colSpan="1/2">
                        <div className="cb-c-store-details-page__show-filter-modal-button fable:text-right">
                          <Button
                            variant="primary"
                            onClick={this.handleFilterModalOpen}
                          >
                            Filter devices
                          </Button>
                        </div>
                      </GridItem>
                    </Grid>

                    <LoadingWrapper isLoading={this.props.isLoadingDevices}>
                      <DeviceList
                        devices={energySubmeterDevices}
                        loadFailed={this.props.energyLoadFailed}
                      />
                    </LoadingWrapper>
                  </GridItem>
                </Grid>
                <Modal
                  isOpen={this.state.filterModalVisible}
                  onClose={() => this.handleFilterModalClose()}
                >
                  <DeviceFilter
                    {...this.props}
                    tagHierarchy={this.props.tagHierarchy}
                    handleFilterUpdated={this.handleFilterUpdated}
                  />
                </Modal>
              </div>
            </div>
          </Tab>
          <Tab
            value="lighting"
            label="Lighting"
            isSelected={this.props.activeTab === "lighting"}
            onClick={() => this.setActiveTab("lighting")}
          >
            <div>
              {this.props.storeStatus ? (
                <div>
                  {" "}
                  <WhenAuthorized roles={[roles.LIGHTING_SETUP]}>
                    <Grid>
                      <GridItem colSpan={{ xs: "1/1", lg: "1/1" }}>
                        <h4>Store Control Panels</h4>
                      </GridItem>
                      {this.props.loadFailed ? (
                        <GridItem colSpan={{ xs: "1/1", lg: "1/1" }}>
                          <ErrorMessage />
                        </GridItem>
                      ) : (
                        <StoreLightingControlPanel
                          storeStatus={this.props.storeStatus}
                          storeId={this.props.store.storeId}
                          loadFailed={this.props.loadFailed}
                        />
                      )}
                    </Grid>
                  </WhenAuthorized>
                  {this.props.storeStatus &&
                  this.props.storeStatus.commissioningStatus.code ===
                    "Completed" ? (
                    <React.Fragment>
                      <div className="fable:pt-4">
                        <StoreLightingStatusPanel
                          storeStatus={this.props.storeStatus}
                          activeScene={this.getActiveScene(
                            this.props.openingHours
                          )}
                        />
                      </div>
                      {this.managersOverrideStatus() ===
                        managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_ON && (
                        <WhenAuthorized
                          roles={[roles.LIGHTING_OVERRIDES_WRITE]}
                        >
                          <ManagersOverrideBanner
                            onSubmit={this.props.onSubmit}
                            onSaveComplete={this.props.onSaveComplete}
                          >
                            <h5>Managers override is on in this store</h5>
                          </ManagersOverrideBanner>
                        </WhenAuthorized>
                      )}
                      {this.managersOverrideStatus() ===
                        managersOverrideStatus.MANAGERS_OVERRIDE_STATUS_DISABLE_PENDING && (
                        <ManagersOverridePendingBanner>
                          <h5>
                            Awaiting confirmation managers override has been
                            turned off
                          </h5>
                        </ManagersOverridePendingBanner>
                      )}

                      <h3 className="fable:pt-4">
                        Lighting schedule and overrides
                      </h3>
                      <WhenAuthorized roles={[roles.LIGHTING_OVERRIDES_READ]}>
                        <StoreOverridesTable
                          data={this.mapOverrides(
                            this.props.overrides.overrides
                          )}
                        />
                      </WhenAuthorized>
                      <StoreSceneLevelsTable
                        zoneSceneLevels={this.mapZoneSceneLevels(
                          this.props.zoneConfiguration
                        )}
                      />
                    </React.Fragment>
                  ) : (
                    <div className="fable:pt-4">
                      This store has not completed the commissioning process.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)", padding: "20px" }}>This store does not have lighting control.</div>
              )}
            </div>
          </Tab>
        </Tabs>
      </div>
    );
  }
}

StoreDetailsPage.propTypes = {
  storeId: PropTypes.string,
  store: PropTypes.object,
  isLoading: PropTypes.bool,
  storeStatus: PropTypes.shape({
    commissioningStatus: PropTypes.shape({
      code: PropTypes.string,
      description: PropTypes.string
    }),
    gatewayOnline: PropTypes.boolean
  }),
  energyLoadFailed: PropTypes.bool,
  lightingLoadFailed: PropTypes.bool,
  onTabChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onSaveComplete: PropTypes.func
};

export default StoreDetailsPage;
